import type { IntlShape } from 'react-intl';
import type { ToolUseItem } from '../../../../contracts/harness.types';

import {
  ComputerUseNotePushRecapCard,
  type ComputerUseNotePushRecapFailure,
  type ComputerUseNotePushRecapSection,
} from '../ComputerUseNotePushRecapCard';
import {
  isRecord,
  readRecordArray,
  readString,
  readToolResultJsonCandidates,
} from './shared/readers';
import { toArtefactStatus } from './status';

interface ComputerUseNotePushRecapData {
  appName?: string;
  recorded: ComputerUseNotePushRecapSection[];
  keptUnchanged: ComputerUseNotePushRecapSection[];
  failed: ComputerUseNotePushRecapFailure[];
}

const hasRecapKeys = (record: Record<string, unknown>): boolean =>
  'recorded' in record || 'kept_unchanged' in record || 'failed' in record;

/**
 * Section headings for a recap list. `recorded` arrives as
 * `{section_heading, ui_targets}` objects, but `kept_unchanged` is a list of
 * plain heading STRINGS (BE:
 * `service/tools/registrations/note_push_targets.py` record handler). One
 * tolerant reader accepts either shape so both render.
 */
const readSectionHeadings = (
  data: Record<string, unknown>,
  key: string
): ComputerUseNotePushRecapSection[] => {
  const raw = data[key];
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    const heading =
      typeof entry === 'string'
        ? entry.trim() || undefined
        : isRecord(entry)
          ? readString(entry, 'section_heading')
          : undefined;
    return heading ? [{ sectionHeading: heading }] : [];
  });
};

/** Failed sections carrying `{section_heading, reason}`. */
const readFailures = (
  data: Record<string, unknown>
): ComputerUseNotePushRecapFailure[] => {
  const rows = readRecordArray(data, 'failed');
  if (!rows) return [];
  return rows.flatMap((row) => {
    const heading = readString(row, 'section_heading');
    if (!heading) return [];
    return [{ sectionHeading: heading, reason: readString(row, 'reason') ?? '' }];
  });
};

/**
 * Read the `record_note_push_hints` result. Uses the shared candidate ladder
 * (live `result_provider_metadata.data`, else JSON parsed out of the result
 * text, else a `{ data: … }` envelope) and picks the first candidate that
 * carries recap keys.
 */
const readRecapData = (item: ToolUseItem): ComputerUseNotePushRecapData | undefined => {
  const candidates = readToolResultJsonCandidates(item);
  const data = candidates.find(hasRecapKeys) ?? candidates[0];
  if (!data) return undefined;

  return {
    appName: readString(data, 'app_name'),
    recorded: readSectionHeadings(data, 'recorded'),
    keptUnchanged: readSectionHeadings(data, 'kept_unchanged'),
    failed: readFailures(data),
  };
};

/**
 * Per-field recap for `record_note_push_hints` — renders what the agent wrote
 * into the EHR note (recorded / kept-unchanged / failed) from the RESULT ROW's
 * structured payload. Never reads the live child-stream trace (ephemeral +
 * lossy). Returns `null` for non-terminal / error / empty states so those fall
 * through to the generic row, matching the `fill_pdf_form` adapter.
 */
export const renderRecordNotePushHintsToolUse = (
  item: ToolUseItem,
  intl: IntlShape
): JSX.Element | null => {
  const status = toArtefactStatus(item.status);
  // Prefer the raw model input casing (e.g. "Cerner") for the title in BOTH
  // states — the completed payload's `app_name` is BE-casefolded, so reading it
  // there would flip the title to "cerner" on completion.
  const inputAppName = readString(item.input, 'app_name');

  if (status === 'generating') {
    return (
      <ComputerUseNotePushRecapCard
        status="generating"
        title={computerUseNotePushTitle(intl, inputAppName)}
        recorded={[]}
        keptUnchanged={[]}
        failed={[]}
      />
    );
  }

  if (status !== 'completed') return null;

  const data = readRecapData(item);
  if (!data) return null;
  if (data.recorded.length + data.keptUnchanged.length + data.failed.length === 0) {
    return null;
  }

  return (
    <ComputerUseNotePushRecapCard
      status="completed"
      title={computerUseNotePushTitle(intl, inputAppName ?? data.appName)}
      recorded={data.recorded}
      keptUnchanged={data.keptUnchanged}
      failed={data.failed}
    />
  );
};

const computerUseNotePushTitle = (
  intl: IntlShape,
  appName: string | undefined
): string =>
  appName
    ? intl.formatMessage(
        { id: 'Pfe3avpyf8', defaultMessage: 'Note mapping · {app}' },
        { app: appName }
      )
    : intl.formatMessage({ id: 'gP8hWVEZ1L', defaultMessage: 'Note mapping' });
